import { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

import { useOrg } from '@/contexts/OrgContext';
import {
  buildChatAboutManyNavState,
  MAX_CHAT_ABOUT_RECORDS,
} from '@/lib/crm/components/badges/buildChatAboutNavState';
import { getDisplayName } from '@/lib/crm/utils/getDisplayName';
import type { CrmObject } from '@/types/crm';

/** Handlers for wiring a "Chat about these records" bulk action. */
export interface ChatAboutRecordsHandlers {
  /** Explicit selection: navigate to chat seeded with the selected records. */
  onClick: (objects: CrmObject[]) => void;
  /** Select-all/filter mode: not supported yet — explains via a toast. */
  onClickWithFilters: () => void;
}

/**
 * Bulk-action handler that opens the conversation composer pre-seeded with the
 * selected records as inline entity mentions, ready for the user to type a
 * question. Reuses the same `<Entity>` mention pipeline as the single-record
 * "Chat about this record" action on the record page.
 *
 * Selection is capped at {@link MAX_CHAT_ABOUT_RECORDS}; over the cap we surface
 * a toast instead of navigating (matching the always-enabled + toast-feedback
 * pattern used by the other CRM bulk/row actions). Select-all/filter mode is
 * intentionally unsupported in this first version.
 */
export function useChatAboutRecords(): ChatAboutRecordsHandlers {
  const navigate = useNavigate();
  const { currentOrg } = useOrg();
  const orgId = currentOrg?.id;

  const onClick = useCallback(
    (objects: CrmObject[]) => {
      if (objects.length === 0 || !orgId) {
        return;
      }

      if (objects.length > MAX_CHAT_ABOUT_RECORDS) {
        toast.info(
          `Select up to ${MAX_CHAT_ABOUT_RECORDS} records to chat about at once.`
        );
        return;
      }

      const entities = objects.map(object => ({
        entityId: object.entity_id,
        entityType: object.entity_type_name,
        displayName: getDisplayName(object),
      }));
      const { pathname, state } = buildChatAboutManyNavState({
        orgId,
        entities,
      });
      navigate(pathname, { state });
    },
    [orgId, navigate]
  );

  const onClickWithFilters = useCallback(() => {
    toast.info(
      `Select up to ${MAX_CHAT_ABOUT_RECORDS} records individually to chat about them.`
    );
  }, []);

  return useMemo(
    () => ({ onClick, onClickWithFilters }),
    [onClick, onClickWithFilters]
  );
}
